/**
 * Central API client for the EliteRentals backend.
 *
 * All data access goes through this file.
 * Set REACT_APP_BACKEND_URL in your .env to point at the backend server.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

async function apiFetch(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  const data = await res.json();

  if (!res.ok) {
    const err = new Error(data.error || 'Request failed');
    err.code = data.code;
    err.status = res.status;
    throw err;
  }

  return data;
}

/** Submit an access request (pending status, no OTP required) */
export const requestAccess = (payload) =>
  apiFetch('/api/dealers/request-access', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/** Check backend API + all DB tables are reachable */
export const checkBackendSetup = () =>
  apiFetch('/api/health/db');

// ── OTP ───────────────────────────────────────────────────────────────────

export const sendOtp = (phoneNumber) =>
  apiFetch('/api/otp/send', {
    method: 'POST',
    body: JSON.stringify({ phoneNumber }),
  });

export const verifyOtp = (phoneNumber, otp, purpose = 'checkout') =>
  apiFetch('/api/otp/verify', {
    method: 'POST',
    body: JSON.stringify({ phoneNumber, otp, purpose }),
  });

// ── Dealers ───────────────────────────────────────────────────────────────

/** Pre-OTP check: returns { data: { id, status } } or { data: null } */
export const getDealerByPhone = (phone) =>
  apiFetch(`/api/dealers/by-phone?phone=${encodeURIComponent(phone)}`);

/** Fetch full dealer record by UUID (used by AuthContext on app load) */
export const getDealerById = (id) =>
  apiFetch(`/api/dealers/${id}`);

/** Register a new dealer account + generate referral link */
export const registerDealer = (payload) =>
  apiFetch('/api/dealers/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/** Get referral code + link for a dealer */
export const getDealerReferral = (dealerId) =>
  apiFetch(`/api/dealers/${dealerId}/referral`);

/** Get dashboard summary stats */
export const getDealerStats = (dealerId) =>
  apiFetch(`/api/dealers/${dealerId}/stats`);

/** Get all orders for a dealer */
export const getDealerOrders = (dealerId) =>
  apiFetch(`/api/dealers/${dealerId}/orders`);

/** Get payout balance + history */
export const getPayoutData = (dealerId) =>
  apiFetch(`/api/dealers/${dealerId}/payouts`);

/** Submit a payout request */
export const requestPayout = (dealerId, amount) =>
  apiFetch(`/api/dealers/${dealerId}/payouts`, {
    method: 'POST',
    body: JSON.stringify({ amount }),
  });
